import React, { useEffect, useRef, useState } from "react";
import formBaseStyles from "../text-input/styles.module.scss";
import styles from "./styles.module.scss";
import CaretDown from "../../../assets/svg/icons/common/CaretDown";
import SearchIcon from "../../../assets/svg/icons/common/search";
import TextInput from "../text-input";
import BlueTick from "../../../assets/svg/icons/common/blue-tick";
import ClearIcon from "../../../assets/svg/icons/common/CloseIcon";
import Button from "../../new-button";
import Spinner from "../../Loaders/Spinner";

const Option = ({ children, option, handleOptionSelect, blank }) => {
  return (
    <div
      tabIndex={0}
      className={`${styles.option} ${blank && styles.blank}`}
      onClick={() => handleOptionSelect(option)}
      onKeyUp={(e) => e.key === "Enter" && handleOptionSelect(option)}
    >
      {children}
    </div>
  );
};

const UserItem = ({ option, selected }) => {
  return (
    <div className={styles.userItem}>
      {selected && (
        <div className={styles.selected}>
          <BlueTick />
        </div>
      )}
      <div className={styles.avatar}>{option.label.charAt(0)}</div>
      <p className={styles.name}>{option.label}</p>
    </div>
  );
};

export const Select = ({
  id,
  name,
  label,
  placeholder,
  prefix,
  suffix,
  value,
  users = false,
  search = false,
  options,
  onChange,
  onBlur,
  error = "",
  errored = false,
  dropdownHeader = "",
  placeholderAsBlankOption = false,
  secondary = false,
  customClass = "",
  disabled = false,
  async = false,
  onSearchChange = null,
  debounceDelay = 250,
}) => {
  const selectContainerRef = useRef(null);
  const searchInputRef = useRef(null);
  const timeoutRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [resultsLoading, setResultsLoading] = useState(false);

  const handleOptionSelect = (option, blank = false) => {
    if (disabled) return;
    setDropdownOpen(false);

    const optionValue = option?.value;
    const valueIsObject = typeof value === "object";

    const isSame =
      blank ||
      (valueIsObject && value?.value == optionValue) ||
      (!valueIsObject && value == optionValue);

    onChange(isSame ? "" : valueIsObject ? option : optionValue);

    // Reset async options if cleared
    if (async && onSearchChange) {
      onSearchChange("");
    }
  };

  const handleOnSearchInputChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);

    if (async && onSearchChange) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        setResultsLoading(true);
        Promise.resolve(onSearchChange(term)).finally(() =>
          setResultsLoading(false),
        );
      }, debounceDelay);
    }
  };

  const handleBlur = (e) => {
    if (
      selectContainerRef.current &&
      !selectContainerRef.current.contains(e.relatedTarget)
    ) {
      setDropdownOpen(false);
      if (onBlur) {
        onBlur({
          target: {
            name,
            value:
              (typeof value === "object" && value?.value) ||
              (typeof value === "string" && value) ||
              "",
          },
          persist: () => {},
        });
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        selectContainerRef.current &&
        !selectContainerRef.current.contains(e.target)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!dropdownOpen) {
      setSearchTerm("");
    }
  }, [dropdownOpen]);

  useEffect(() => {
    if (dropdownOpen && async && search) {
      const timeout = setTimeout(() => {
        if (searchInputRef?.current?.focus) {
          searchInputRef.current.focus();
        }
      }, 100);
      // I do not like this, but it works.
      // This probably has something to do with input is
      // not being rendered yet.

      return () => clearTimeout(timeout);
    }
  }, [dropdownOpen, async, search]);

  const handleBaseInputClick = () => {
    if (disabled) return;
    setDropdownOpen((prev) => !prev);
  };

  const getSelectedValue = () => {
    if (!value) return placeholder;

    if (typeof value === "object" && value.label) {
      return value.label;
    }

    const matched = options?.find((opt) => opt?.value === value);

    if (!matched && value?.label && async) {
      return value.label;
    }

    if (matched) {
      return matched?.label;
    }

    return placeholder;
  };

  return (
    <div
      className={`${styles.selectContainer} ${secondary && styles.secondary} ${customClass} ${disabled ? styles.disabled : ""} ${error ? styles.errored : ""}`}
      ref={selectContainerRef}
    >
      {label && (
        <label className={formBaseStyles.label} htmlFor={id}>
          {label}
        </label>
      )}

      {value && (
        <div className={`${styles.clearButton}`}>
          <Button
            icon={<ClearIcon />}
            variant="ghost"
            size="small"
            onClick={() => handleOptionSelect("", true)}
          />
        </div>
      )}

      <div
        className={`${formBaseStyles.input} ${styles.input}`}
        tabIndex={disabled ? -1 : 0}
        onClick={handleBaseInputClick}
        onKeyUp={(e) =>
          !disabled && e.key === "Enter" && setDropdownOpen((prev) => !prev)
        }
        onBlur={handleBlur}
      >
        <p className={styles.inputSelection}>{getSelectedValue()}</p>

        <div className={`${styles.caret} ${dropdownOpen && styles.open}`}>
          <CaretDown />
        </div>
      </div>

      <div
        className={`${styles.dropdownContainer} ${dropdownOpen && styles.open}`}
      >
        {search && (
          <div className={styles.searchField}>
            <SearchIcon />
            <TextInput
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleOnSearchInputChange}
              className={`${formBaseStyles.input} ${styles.searchInput}`}
            />
            {resultsLoading && (
              <div className={styles.resultsLoadingSpinner}>
                <Spinner size={18} />
              </div>
            )}
          </div>
        )}

        <div className={`${styles.dropdown}`}>
          {!options && <div className={styles.option}>No options</div>}

          {options && dropdownHeader && (
            <span className={styles.dropdownHeader}>{dropdownHeader}</span>
          )}

          {options && placeholderAsBlankOption && searchTerm.length === 0 && (
            <Option
              key={`blank-option-${label}`}
              option={{ value: "", label: placeholder }}
              handleOptionSelect={handleOptionSelect}
              blank
            >
              {placeholder}
            </Option>
          )}

          {options && searchTerm.length > 0 && (
            <>
              {(async && options.length === 0) ||
              (!async &&
                options.filter((option) =>
                  option?.label
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase()),
                ).length === 0) ? (
                <div className={styles.option}>No results found</div>
              ) : null}
            </>
          )}

          {options && (
            <>
              {(async
                ? options
                : options.filter(
                    (option) =>
                      !searchTerm ||
                      option?.label
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase()),
                  )
              ).map((option) => (
                <Option
                  key={option.value}
                  option={option}
                  handleOptionSelect={handleOptionSelect}
                >
                  {users ? (
                    <UserItem
                      option={option}
                      selected={
                        value?.value == option.value || value == option.value
                      }
                    />
                  ) : (
                    option.label
                  )}
                </Option>
              ))}
            </>
          )}
        </div>
      </div>

      {error && <span className={formBaseStyles.error}>{error}</span>}
    </div>
  );
};

export default Select;
